import * as React from 'react';
import { Button, Popconfirm, Radio } from 'antd';
import {
    BookOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps } from 'theme/jss-types';
import { PickupAddress } from 'components/pickup/create-pickup/pickup.types';
import Helper from 'library/Helper';
import { addressFieldStyles } from './address-field.styles';
import AddressDetailsModal from './AddressDetailsModal';

interface AddressCardProps extends StylesProps<typeof addressFieldStyles> {
    item: PickupAddress;
    addressKey: string;
    isSelected: boolean;
    isReadOnly?: boolean;
    showAddressCode: boolean;
    primaryColor: string;
    allowDefaultSourceAddress: boolean;
    defaultMultipleLimit: any;
    defaultAddress: Record<any, any>;
    currentAddressType: string;
    onSelect: (e: any) => void;
    onEdit: () => void;
    onSaveDefault: (address: PickupAddress, isMultiple?: boolean) => void;
    onConfirmDelete: (e: any, item: PickupAddress) => void;
}

const AddressCardComponent = (props: AddressCardProps) => {
    const { t } = useTranslation();
    const {
        classes,
        item,
        addressKey,
        isSelected,
        isReadOnly = false,
        showAddressCode,
        primaryColor,
        allowDefaultSourceAddress,
        defaultMultipleLimit,
        defaultAddress,
        currentAddressType,
        onSelect,
        onEdit,
        onSaveDefault,
        onConfirmDelete,
    } = props;

    const [detailsVisible, setDetailsVisible] = React.useState(false);
    const customerCode = window.localStorage.getItem('userCode');
    const showActions = !isReadOnly && !['oldDstAddress', 'oldSrcAddress'].includes(item?.id);

    const cardStyle: React.CSSProperties = isSelected
        ? { border: `1px solid ${primaryColor}`, backgroundColor: '#E7F5FF', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }
        : {};

    const renderDefaultControl = () => {
        if (!allowDefaultSourceAddress) return null;

        if (defaultMultipleLimit) {
            const isMultipleDefault = currentAddressType === 'srcAddress'
                ? item?.isDefaultSrc : item?.isDefaultDst;
            return (
                <>
                    {isMultipleDefault ? (
                        <Button style={{ cursor: 'default' }} shape="round">Default</Button>
                    ) : null}
                    {isMultipleDefault ? (
                        <Popconfirm
                            placement="topLeft"
                            title="Are you sure you want to remove this default Address"
                            onConfirm={(e) => {
                                Helper.stops(e as any);
                                onSaveDefault({
                                    ...item,
                                    isDefaultSrc: currentAddressType === 'srcAddress' ? false : item?.isDefaultSrc,
                                    isDefaultDst: currentAddressType !== 'srcAddress' ? false : item?.isDefaultDst,
                                }, true);
                            }}
                            onCancel={(e) => Helper.stops(e as any)}
                        >
                            <BookOutlined
                                style={{ color: 'red' }}
                                onClick={(e) => { Helper.stops(e as any); }}
                            />
                        </Popconfirm>
                    ) : (
                        <BookOutlined
                            onClick={(e) => {
                                Helper.stops(e as any);
                                onSaveDefault({
                                    ...item,
                                    isDefaultSrc: currentAddressType === 'srcAddress' ? true : item?.isDefaultSrc,
                                    isDefaultDst: currentAddressType !== 'srcAddress' ? true : item?.isDefaultDst,
                                }, true);
                            }}
                            style={{ color: primaryColor }}
                        />
                    )}
                </>
            );
        }

        // eslint-disable-next-line no-underscore-dangle
        if (defaultAddress._id === item.id) {
            return <Button style={{ cursor: 'default' }} shape="round">Default</Button>;
        }
        return (
            <BookOutlined
                onClick={(e) => { Helper.stops(e as any); onSaveDefault(item); }}
                style={{ color: primaryColor }}
            />
        );
    };

    const addressLines = [
        item.addressLine1, item.addressLine2, item.addressLine3, item.addressLine4, item.addressLine5,
    ].filter(Boolean).join(', ');
    const cityLine = [item.pincode, item.cityName].filter(Boolean).join(' - ');
    const stateLine = [item.stateName, item.countryName].filter(Boolean).join(', ');

    return (
        <div
            key={item.id}
            className={classes.card}
            style={cardStyle}
            onClick={onSelect}
        >
            <Radio
                name={`${addressKey}-radio`}
                checked={isSelected}
                className={classes.radio}
                onChange={onSelect}
            />

            <div className={classes.info}>
                <div className={classes.infoName}>
                    {showAddressCode && item.addressCode ? (
                        <span style={{ fontWeight: 'bold' }}>
                            {item.addressCode}
                            <span style={{ margin: '0 6px 0 4px' }}> | </span>
                        </span>
                    ) : null}
                    <span>{item.name || ''}</span>
                </div>
                <div className={classes.infoDetails}>
                    <div className={classes.addressBlock}>
                        {addressLines ? <div>{addressLines}</div> : null}
                        {(cityLine || stateLine) ? (
                            <div>
                                {cityLine}
                                {cityLine && stateLine ? ', ' : ''}
                                {stateLine}
                            </div>
                        ) : null}
                        {item.locationId ? (
                            <div>
                                <b>{item.locationId}</b>
                                {' '}
                                <b>{item.locationCode || `${customerCode}_${item.locationId}`}</b>
                            </div>
                        ) : null}
                    </div>
                    <div className={classes.contactBlock}>
                        {item.phone ? <div>{item.phone}</div> : null}
                        {item.alternatePhone ? <div>{item.alternatePhone}</div> : null}
                        {item.alternatePhone2 ? <div>{item.alternatePhone2}</div> : null}
                        {item.email ? <div>{item.email}</div> : null}
                    </div>
                </div>
            </div>

            <div className={classes.actions}>
                <InfoCircleOutlined
                    onClick={(e) => { Helper.stops(e as any); setDetailsVisible(true); }}
                    style={{ color: primaryColor }}
                />
            </div>

            {showActions && (
                <div className={classes.actions}>
                    {renderDefaultControl()}
                    <EditOutlined
                        onClick={(e) => { Helper.stops(e as any); onEdit(); }}
                        style={{ color: primaryColor }}
                    />
                    <Popconfirm
                        placement="topLeft"
                        title={t('delete_address_text')}
                        disabled={isSelected}
                        onConfirm={(e) => onConfirmDelete(e, item)}
                        okText={t('ok_text')}
                        cancelText={t('cancel')}
                        onCancel={(e) => Helper.stops(e as any)}
                    >
                        <DeleteOutlined
                            disabled={isSelected}
                            onClick={(e) => { Helper.stops(e as any); }}
                            style={{ color: isSelected ? '#ccc' : 'red' }}
                        />
                    </Popconfirm>
                </div>
            )}

            <AddressDetailsModal
                item={item}
                visible={detailsVisible}
                onClose={() => setDetailsVisible(false)}
            />
        </div>
    );
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: addressFieldStyles,
    },
};

export default GenericHoc(hocConfig)(AddressCardComponent);
